import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: 40,
        }}>
          <div style={{
            textAlign: 'center',
            maxWidth: 420,
          }}>
            <div style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background: 'var(--red-soft, rgba(220,38,38,0.08))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--red, #DC2626)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h2 style={{
              fontSize: 18,
              fontWeight: 700,
              color: 'var(--t1)',
              fontFamily: 'var(--font-primary)',
              margin: '0 0 8px',
            }}>
              Something went wrong
            </h2>
            <p style={{
              fontSize: 13,
              color: 'var(--t2)',
              fontFamily: 'var(--font-primary)',
              margin: '0 0 6px',
              lineHeight: 1.5,
            }}>
              An unexpected error occurred. Try refreshing or navigating back.
            </p>
            {this.state.error && (
              <p style={{
                fontSize: 11,
                color: 'var(--t3)',
                fontFamily: 'var(--font-mono)',
                margin: '0 0 20px',
                padding: '8px 12px',
                background: 'var(--bg, #F5F6FA)',
                borderRadius: 8,
                wordBreak: 'break-word',
              }}>
                {this.state.error.message}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button
                onClick={this.handleReset}
                style={{
                  padding: '8px 20px',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'var(--font-primary)',
                  color: '#fff',
                  background: 'var(--accent, #D23A3B)',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                Try Again
              </button>
              <button
                onClick={() => { window.location.href = '/'; }}
                style={{
                  padding: '8px 20px',
                  fontSize: 13,
                  fontWeight: 500,
                  fontFamily: 'var(--font-primary)',
                  color: 'var(--t2)',
                  background: 'var(--hover, #F0F1F5)',
                  border: '1px solid var(--card-border)',
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
